import { CrossIcon, Plus, Star } from 'assets/icons';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import DatePicker from 'react-datepicker';

const FilterPanel = ({
  isOpen, onClose,
  filterConfigs, filterValues, sortedFilterKeys,
  expandedFilter, setExpandedFilter,
  getFilterDisplayValue, setFilterValue, clearFilterValue,
  cityOptions, filterListRef,
  onReset, onApply,
  activeFilterCount,
}) => {
  if (!isOpen) return null;

  const renderFilterBody = (filterKey, config) => {
    switch (config.type) {
      case 'select':
        return (
          <div className="flex flex-col gap-1">
            {config.options.length > 0 ? (
              config.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { setFilterValue(filterKey, opt.value); setExpandedFilter(null); }}
                  className={`text-left px-3 py-2 rounded-md ft-sm transition-colors ${
                    filterValues[filterKey] === opt.value
                      ? 'bg-yellow-50 text-yellow-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))
            ) : (
              <input
                type="text"
                placeholder={config.placeholder || 'Rechercher...'}
                value={filterValues[filterKey] || ''}
                onChange={(e) => setFilterValue(filterKey, e.target.value)}
                className="w-full h-9 px-3 ft-sm border border-gray-200 rounded-md focus:border-yellow-500 focus:outline-none"
              />
            )}
          </div>
        );

      case 'selectMultipleCheckbox':
        return (
          <div className="flex flex-col gap-3">
            <StyledInput
              type="selectMultipleCheckbox"
              placeholder={config.placeholder || 'Sélectionner...'}
              list={filterValues[filterKey] || config.options.map((v) => ({ ...v, checked: false }))}
              value={
                (filterValues[filterKey] || []).some((v) => v.checked)
                  ? (filterValues[filterKey] || []).filter((v) => v.checked).map((v) => v.label).join(', ')
                  : config.placeholder || 'Sélectionner...'
              }
              onChange={(list) => setFilterValue(filterKey, list)}
              isSearchable showChevron showCheck dropdownFullWidth
            />
            {filterKey === 'department' && cityOptions.length > 0 && (
              <StyledInput
                type="selectMultipleCheckbox"
                placeholder="Sélectionner une ville"
                list={filterValues.city || cityOptions.map((v) => ({ ...v, checked: false }))}
                value={
                  (filterValues.city || []).some((v) => v.checked)
                    ? (filterValues.city || []).filter((v) => v.checked).map((v) => v.label).join(', ')
                    : 'Sélectionner une ville'
                }
                onChange={(list) => setFilterValue('city', list)}
                isSearchable showChevron showCheck dropdownFullWidth
              />
            )}
          </div>
        );

      case 'toggle':
        return (
          <div className="flex gap-2">
            {config.options.map((opt) => {
              const isActive = config.exclusive
                ? filterValues[filterKey] === opt.value
                : filterValues[filterKey]?.[opt.value] !== false;
              return (
                <button
                  key={String(opt.value)}
                  onClick={() => {
                    if (config.exclusive) {
                      const newVal = filterValues[filterKey] === opt.value ? null : opt.value;
                      setFilterValue(filterKey, newVal);
                      if (newVal !== null) setExpandedFilter(null);
                    } else {
                      setFilterValue(filterKey, {
                        ...config.options.reduce((acc, o) => ({
                          ...acc, [o.value]: filterValues[filterKey]?.[o.value] !== false,
                        }), {}),
                        [opt.value]: !isActive,
                      });
                    }
                  }}
                  className={`flex-1 py-2 px-3 rounded-md ft-sm font-medium transition-colors border ${
                    isActive ? 'bg-yellow-50 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-400'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );

      case 'dateRange':
        return (
          <div className="flex flex-col gap-3">
            {['start', 'end'].map((bound) => (
              <div key={bound} className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">{bound === 'start' ? 'À partir du' : "Jusqu'au"}</span>
                <DatePicker
                  selected={filterValues[filterKey]?.[bound] ? new Date(filterValues[filterKey][bound] + 'T00:00:00') : null}
                  onChange={(date) => {
                    if (!date) return setFilterValue(filterKey, { ...filterValues[filterKey], [bound]: null });
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    setFilterValue(filterKey, { ...filterValues[filterKey], [bound]: `${y}-${m}-${d}` });
                  }}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="jj/mm/aaaa"
                  isClearable
                  minDate={bound === 'end' && filterValues[filterKey]?.start ? new Date(filterValues[filterKey].start + 'T00:00:00') : null}
                  maxDate={bound === 'start' && filterValues[filterKey]?.end ? new Date(filterValues[filterKey].end + 'T00:00:00') : null}
                  className="w-full h-9 px-3 ft-sm border border-gray-200 rounded-md focus:border-yellow-500 focus:outline-none"
                  locale="fr-FR"
                />
              </div>
            ))}
          </div>
        );

      case 'rating':
        return (
          <div className="flex flex-col gap-2">
            {['min', 'max'].map((bound) => (
              <div key={bound} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-6">{bound === 'min' ? 'Min' : 'Max'}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => {
                        const other = bound === 'min' ? 'max' : 'min';
                        const otherVal = filterValues[filterKey]?.[other];
                        const clamp = bound === 'min' && otherVal !== undefined && star > otherVal
                          ? { [other]: star }
                          : bound === 'max' && otherVal !== undefined && star < otherVal
                            ? { [other]: star }
                            : {};
                        setFilterValue(filterKey, { ...filterValues[filterKey], [bound]: star, ...clamp });
                      }}
                      className="p-0.5 transition-transform hover:scale-110"
                    >
                      <Star wh={20} color={star <= (filterValues[filterKey]?.[bound] || 0) ? '#F59E0B' : '#D1D5DB'} />
                    </button>
                  ))}
                </div>
                {filterValues[filterKey]?.[bound] && (
                  <span className="text-xs text-gray-500">{filterValues[filterKey][bound]}/5</span>
                )}
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-gray-900 opacity-50 transition-opacity duration-300" />
      <div
        className="absolute right-0 top-0 h-full w-full max-w-[520px] bg-white shadow-xl z-50 flex flex-col animate-slideR"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between p-5 gap-5">
          <h3 className="ft-lg text-[#202124] font-semibold my-auto flex-1 min-w-0 flex items-center gap-2">
            Filtres
            {activeFilterCount > 0 && (
              <div className="bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px] ft-sm">
                {activeFilterCount}
              </div>
            )}
          </h3>
          <button type="button" className="flex items-center justify-center border rounded p-1" onClick={onClose}>
            <CrossIcon wh={20} color="#111827" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4" ref={filterListRef}>
          <div className="flex flex-col gap-2">
            {sortedFilterKeys.map((filterKey, index) => {
              const config = filterConfigs[filterKey];
              const isExpanded = expandedFilter === filterKey;
              const displayValue = getFilterDisplayValue(filterKey);
              const hasValue = displayValue !== null;
              const isFirstInactive = index > 0 && !hasValue && getFilterDisplayValue(sortedFilterKeys[index - 1]) !== null;

              if (isExpanded) {
                return (
                  <div key={filterKey} className="border border-yellow-400 rounded-md overflow-hidden bg-white sh-sm">
                    <div className="flex items-center justify-between px-4 py-3 bg-yellow-50">
                      <span className="ft-sm font-medium text-gray-900">{config.label}</span>
                      <button onClick={() => setExpandedFilter(null)} className="p-0.5 rounded hover:bg-yellow-100 transition-colors">
                        <CrossIcon wh={14} color="#374151" />
                      </button>
                    </div>
                    <div className="px-4 py-3">{renderFilterBody(filterKey, config)}</div>
                  </div>
                );
              }

              if (isFirstInactive) {
                return <div key={`divider-${filterKey}`} className="my-2 border-t border-gray-200" />;
              }

              if (hasValue) {
                return (
                  <div
                    key={filterKey}
                    className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md cursor-pointer hover:bg-yellow-100 transition-colors"
                    onClick={() => setExpandedFilter(filterKey)}
                  >
                    <div className="flex flex-col">
                      <span className="ft-sm font-medium text-gray-900">{config.label}</span>
                      <span className="text-xs text-yellow-700">{displayValue}</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); clearFilterValue(filterKey); }} className="p-1 rounded hover:bg-yellow-200 transition-colors">
                      <CrossIcon wh={14} color="#374151" />
                    </button>
                  </div>
                );
              }

              return (
                <button
                  key={filterKey}
                  onClick={() => setExpandedFilter(filterKey)}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                >
                  <span className="ft-sm text-gray-600">{config.label}</span>
                  <Plus wh={16} color="#9CA3AF" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex gap-3">
          <Button color="SECONDARY" size="SM" className="flex-1" onClick={onReset}>Réinitialiser</Button>
          <Button color="PRIMARY" size="SM" className="flex-1" onClick={onApply}>Appliquer</Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
